import { useState, useContext, useEffect } from 'react';
import { AddIcon, ICON_SIZE, ViewIcon } from "./actionIcons";
import { Theme } from '@radix-ui/themes';
import MessageTemplateEditor from './MessageTemplateEditor';
import { GlobalContext } from "./GlobalContext";
import JoyridePortal from './JoyridePortal';
import { HelpCircle } from 'lucide-react';
import useMessagingTour from './useMessagingTour';
import CustomTooltip from './JoyrideCustomTooltip';
import { emitter } from './UtilityFunctions';
import { apiFetch } from './api';
import { TOUR_ANCHORS } from "./tourAnchors";

/**
 * The modal composer.
 *
 * This was a two-tab shell — Compose and Message History — but history was only
 * ever reachable by opening a deal you did not want to message, and it only ever
 * showed the one sheet you opened it from. It now lives on the Messages page,
 * and what is left here is the thing the modal is actually for: writing a
 * message about the record in front of you.
 */
export default function MessageTabs({ recordsData = [], onClose, sheetName }) {
  const { DealershipID, UserID, getMsgBoxResult } = useContext(GlobalContext);
  const [isSending, setIsSending] = useState(false);
  const [sentMessages, setSentMessages] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [currentRequestCleanup, setCurrentRequestCleanup] = useState(null);
  const { runTour, stepIndex, steps, handleStartTutorial, handleJoyrideCallback } = useMessagingTour(showPreview);

  // Close the modal and land on the Messages page's history. The emitter is the
  // app's existing cross-tree navigation channel (CarsNav registers the handler
  // and runs the unsaved-changes guard), so nothing new is needed to get there.
  const goToHistory = () => {
    onClose?.();
    emitter.emit('navigate', { tab: 'tab-messages', subMenu: 'History' });
  };

// Handle sending messages
const handleSendMessages = async (messagesPayload) => {
  // Validation
  if (!messagesPayload?.messages?.length) {
    setSentMessages({
      success: false,
      error: 'No messages to send'
    });
    return;
  }

  // Create abort controller for cleanup
  const abortController = new AbortController();

  try {
    setIsSending(true);
    setSentMessages(null); // Reset previous state

    // No DealershipID or UserID: the server takes both from the session now.
    // Sending them was how a signed-in user could have charged another
    // dealership's SMS account, and under the credit wallet it would decide
    // whose credits were spent.
    const requestBody = {
      sheetName,
      messages: messagesPayload.messages,
      templateId: messagesPayload.templateId,
      templateContent: messagesPayload.templateContent
    };

    //Use the message box to get confirmation before sending
    const userConfirmed = await getMsgBoxResult({
      on: true,
      title: 'Confirm Send Messages',
      message: `Are you sure you want to send ${messagesPayload.messages.length} messages?`,
      buttons: ["YES", "CANCEL"],
    });

    if (userConfirmed==="CANCEL") return;
    
    // apiFetch, not a bare fetch to a hardcoded origin: it carries the CSRF
    // token every other write in the app sends, and honours VITE_API_BASE_URL so
    // this works in dev and under test.
    const response = await apiFetch('/messages/send', {
      method: 'POST',
      signal: abortController.signal,
      body: requestBody,
    });

    // Handle non-ok responses. The server always answers JSON here, so read the
    // `error` field rather than the raw body — response.text() was putting the
    // whole `{"success":false,...}` blob on screen as the user-facing message,
    // and it also defeated the credentials hint below, which matches on wording.
    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      let credentialsMissing = false;
      let capExceeded = false;
      let usage = null;

      try {
        const errorBody = await response.json();
        errorMessage = errorBody.error || errorBody.message || errorMessage;
        credentialsMissing = !!errorBody.credentialsMissing;
        capExceeded = !!errorBody.capExceeded;
        usage = errorBody.usage || null;
      } catch (parseError) {
        console.warn('Could not parse error response:', parseError);
      }

      const err = new Error(errorMessage);
      // Explicit flags from the server, so the hints no longer depend on the
      // exact phrasing of a message that has already drifted once.
      err.credentialsMissing = credentialsMissing;
      err.capExceeded = capExceeded;
      err.usage = usage;
      throw err;
    }

    // Parse response
    let responseData = {};
    const contentType = response.headers.get('Content-Type');

    if (contentType?.includes('application/json')) {
      try {
        responseData = await response.json();
      } catch (parseError) {
        console.warn('Could not parse JSON response:', parseError);
        // Continue with empty object - some endpoints might not return JSON
      }
    }

    // Validate response structure
    if (responseData.success === false) {
      throw new Error(responseData.error || responseData.message || 'Server reported failure');
    }

    // Success handling
    console.log('Messages sent successfully:', responseData);

    setSentMessages({
      success: true,
      count: messagesPayload.messages.length,
      data: responseData
    });

    // The result screen replaces the composer; it carries a "View in Messages"
    // button for anyone who wants the detail, rather than dragging them to a
    // history they usually do not need to look at.

  } catch (error) {
    // Handle different error types
    let errorMessage = 'An unexpected error occurred';

    if (error.name === 'AbortError') {
      errorMessage = 'Request was cancelled';
    } else if (error.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    console.error('Error sending messages:', error);

    setSentMessages({
      success: false,
      error: errorMessage,
      credentialsMissing: !!error.credentialsMissing,
      capExceeded: !!error.capExceeded,
      usage: error.usage || null,
      timestamp: new Date().toISOString()
    });
  } finally {
    setIsSending(false);
  }

  // Cleanup function that can be called externally if needed
  return () => {
    abortController.abort();
  };
};

  // Reset message sent state
  const handleReset = () => {
    setSentMessages(null);
  };


  useEffect(() => {
    return () => {
      if (currentRequestCleanup) {
        currentRequestCleanup();
      }
    };
  }, [currentRequestCleanup]);

  return (
    <Theme>
      <div className="flex flex-col w-full h-full md:h-[455px]">
        <div className="flex justify-end border-b mb-2 flex-shrink-0">
          <button
            id={TOUR_ANCHORS.startMessagingTourButton}
            onClick={handleStartTutorial}
            className="btn-t btn-t-ghost"
            title="Start messaging tour"
          >
            <HelpCircle size={16} />
            <span>Tour</span>
          </button>
        </div>

        <div className="flex-grow overflow-hidden">
          {sentMessages ? (
            <div className="text-center py-8">
              <div className="text-xl font-semibold mb-2">
                {sentMessages.success
                  ? `Successfully sent ${sentMessages.count} messages!`
                  : sentMessages.capExceeded
                    ? '⚠️ Monthly SMS limit reached'
                    : sentMessages.credentialsMissing
                      ? '⚠️ SMS credentials not configured'
                      : `Failed to send messages: ${sentMessages.error}`}
              </div>
              {/* Nothing was sent when the cap is hit — the batch is refused
                  whole rather than partially delivered, so say so plainly. */}
              {!sentMessages.success && sentMessages.capExceeded && (
                <p className="text-sm text-gray-500 mt-1">
                  {sentMessages.error}{' '}
                  <strong>No messages were sent.</strong> Contact your administrator if you need a higher limit.
                </p>
              )}
              {!sentMessages.success && sentMessages.credentialsMissing && (
                <p className="text-sm text-gray-500 mt-1">
                  Please add your EgoSMS account name and password in <strong>Settings → Messaging</strong> before sending messages.
                </p>
              )}
              <div className="mt-4">
                <button
                  onClick={goToHistory}
                  className="btn-t btn-t-primary mr-2"
                ><ViewIcon size={ICON_SIZE} />
                  View in Messages
                </button>
                <button
                  onClick={handleReset}
                  className="btn-t btn-t-secondary"
                ><AddIcon size={ICON_SIZE} />
                  Compose New Message
                </button>
              </div>
            </div>
          ) : (
            <MessageTemplateEditor
              recordsData={recordsData}
              onSendMessages={async (messagesPayload)=>{
                const cleanup = await handleSendMessages(messagesPayload);
                setCurrentRequestCleanup(() => cleanup);
              }}
              isSending={isSending}
              DealershipID={DealershipID}
              UserID={UserID}
              sheetName={sheetName}
              showPreview={showPreview}
              setShowPreview={setShowPreview}
            />
          )}
        </div>
      </div>

      <JoyridePortal
        run={runTour} //
        stepIndex={stepIndex} //
        steps={steps} //
        callback={handleJoyrideCallback}//
        continuous//
        showProgress//
        showSkipButton //
        tooltipComponent={CustomTooltip} //
        styles={{ //
          options: {
            zIndex: 10000,
          },
        }}
        disableScrolling={true}
        disableOverlayClose={true}//
        disableCloseOnEsc={true} //
        spotlightClicks={false}//
        //hideCloseButton={false}
        spotlightPadding={0}  //
      />      
    </Theme>
  );
}